import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DemoChip } from "../components/DemoChip";
import { pageSizes, timing } from "../config";
import { InlineTxLink, TxLink } from "../components/ExplorerLink";
import { TokenIcon } from "../components/TokenIcon";
import { api, DEMO_MAKER, type OrderFill, type OrderRecord } from "../lib/api";
import { formatBaseUnits, formatBaseUnitsExact } from "../lib/amount";
import { getAddress, shortAddress } from "../lib/auth";
import { cancelIntent, CancelError, type CancelStage } from "../lib/cancelIntent";
import { EDIT_INTENT_STATE_KEY, type EditIntent } from "../lib/editIntent";
import { errorReporter } from "../lib/errorReporter";
import { formatRelativeTime, shortHash } from "../lib/format";
import { readBalance } from "../lib/erc20";
import { describeExpiry, expiryInstant } from "../lib/orderTraits";
import { decimalsBySymbol, tokenAddress, tokenBySymbol } from "../lib/tokens";
import { explainWalletError, isUserRejected, toWalletError } from "../lib/wallet";

/** Statuses that are over: nothing left to cancel, nothing left to replace. */
const COMPLETED_STATUSES = new Set(["filled", "cancelled", "canceled"]);
const TERMINAL_STATUSES = new Set([...COMPLETED_STATUSES, "expired"]);

/** Cancelling is a paid transaction, and the only thing that makes an order unfillable. */
const GAS_WARNING =
  "Cancelling costs gas — it invalidates the order on-chain so it can never be filled.";

/** What a demo session actually gets, spelled out where the button is. */
const DEMO_NOTE =
  "This only removes the intent from the gateway's book — it is not an on-chain cancellation.";

/** The gateway's `display` legs; a row stored without them shows raw amounts and "?" symbols. */
function viewOf(record: OrderRecord) {
  return (
    record.display ?? {
      sellToken: "?",
      buyToken: "?",
      sellAmount: record.order.makingAmount ?? "0",
      buyAmount: record.order.takingAmount ?? "0",
    }
  );
}

/** Newest indexed event carrying a receipt, or undefined. */
function newestFill(record: OrderRecord): (OrderFill & { txHash: string }) | undefined {
  const fills = record.fills;
  if (!Array.isArray(fills)) return undefined;
  return fills.find(
    (f): f is OrderFill & { txHash: string } => typeof f?.txHash === "string" && f.txHash !== "",
  );
}

export interface FillProgress {
  /** Base units already filled (`makingAmount - remainingMakingAmount`). */
  filled: string;
  /** Base units still available. */
  remaining: string;
  /** 0–100, rounded for display only — never used to derive an amount. */
  percent: number;
}

/**
 * Fill progress from the gateway's two stored amounts, or null when either is
 * unreadable: unknown progress must not read as "0% filled".
 */
export function fillProgress(record: OrderRecord): FillProgress | null {
  const makingRaw = record.order?.makingAmount;
  const remainingRaw = record.remainingMakingAmount;
  if (typeof makingRaw !== "string" || typeof remainingRaw !== "string") return null;
  if (!/^\d+$/.test(makingRaw) || !/^\d+$/.test(remainingRaw)) return null;
  const making = BigInt(makingRaw);
  const remaining = BigInt(remainingRaw);
  if (making === 0n || remaining > making) return null;
  const filled = making - remaining;
  // Integer maths on base units; the float only ever reaches a CSS width.
  const percent = Number((filled * 10_000n) / making) / 100;
  return { filled: filled.toString(), remaining: remaining.toString(), percent };
}

/** Bar + "12.50 / 100.00 EURC" — a number alone hides how far along an order is. */
function FillCell({ record }: { record: OrderRecord }) {
  const view = viewOf(record);
  const decimals = decimalsBySymbol(view.sellToken);
  const progress = fillProgress(record);
  if (progress === null) {
    return (
      <span className="fill-unknown" title="This gateway does not report fill progress">
        —
      </span>
    );
  }
  const percent = Math.round(progress.percent);
  return (
    <span className="fill-cell">
      <span
        className="fill-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={`${percent}% filled`}
      >
        <span className="fill-bar-value" style={{ width: `${progress.percent}%` }} />
      </span>
      <span className="fill-text">
        {formatBaseUnits(progress.filled, decimals)} /{" "}
        {formatBaseUnits(record.order.makingAmount, decimals)} {view.sellToken}
      </span>
    </span>
  );
}

/** Normalises a gateway status into the pill's modifier class. */
function statusSlug(status: string): string {
  return status.toLowerCase().replace(/[^a-z]+/g, "-");
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`status-pill status-${statusSlug(status)}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

/** Pair cell: both icons and both symbols, sell first. */
function PairCell({ sellToken, buyToken }: { sellToken: string; buyToken: string }) {
  return (
    <span className="pair-cell">
      <span className="pair-icons">
        <TokenIcon token={tokenBySymbol(sellToken)} size={24} />
        <TokenIcon token={tokenBySymbol(buyToken)} size={24} />
      </span>
      <span className="pair-symbols">
        {sellToken} → {buyToken}
      </span>
    </span>
  );
}

/** Live state of the one cancel that can be in flight at a time. */
interface CancelState {
  record: OrderRecord;
  stage: CancelStage | null;
  txHash: string | null;
  error: string | null;
  /** Set when the chain accepted the cancel but the book still shows it. */
  bookWarning: boolean;
  done: boolean;
}

/**
 * The connected wallet's own intents. Cancel is never one click: it costs gas and
 * is the only thing that makes a signed order unfillable. Edit is cancel + re-sign.
 */
export function OrdersPage() {
  const navigate = useNavigate();
  const walletAddress = getAddress();
  const isDemo = walletAddress === null;
  // A demo session's intents are stored under the shared demo maker.
  const maker = walletAddress ?? DEMO_MAKER;

  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [cancelState, setCancelState] = useState<CancelState | null>(null);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      try {
        const list = await api.listOrders({
          maker,
          limit: pageSizes.orders,
          ...(opts.silent === true ? { silent: true } : {}),
        });
        setOrders(Array.isArray(list) ? list : []);
        setRefreshFailed(false);
      } catch {
        // The first load was reported by the api wrapper, refreshes are silent; the list stays.
        setRefreshFailed(true);
      } finally {
        setLoaded(true);
      }
    },
    [maker],
  );

  // Visibility-aware: a backgrounded tab polls nothing.
  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void load({ silent: true });
    }, timing.ordersPollMs);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  // One reading per render: the labels are coarse ("in 2 h"), and the 10 s refresh moves them.
  const now = Date.now();

  /**
   * An order the maker can no longer cover sits open forever with no reason in
   * the book; one balance read per sell token answers it.
   */
  const [balances, setBalances] = useState<Record<string, bigint | null>>({});

  useEffect(() => {
    const maker = getAddress();
    if (maker === null) return;
    const symbols = new Set(
      orders
        .filter((record) => !TERMINAL_STATUSES.has(record.status?.toLowerCase() ?? ""))
        .map((record) => viewOf(record).sellToken),
    );
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        [...symbols].map(async (symbol) => {
          const address = tokenAddress(symbol);
          if (address === null) return [symbol, null] as const;
          return [symbol, await readBalance(address, maker)] as const;
        }),
      );
      if (!cancelled) setBalances(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [orders]);

  const rows = orders.map((record) => ({
    record,
    view: viewOf(record),
    expiry: COMPLETED_STATUSES.has(record.status?.toLowerCase() ?? "")
      ? null
      : describeExpiry(record.order?.makerTraits, now),
    expiresAt: expiryInstant(record.order?.makerTraits),
    terminal: TERMINAL_STATUSES.has(record.status?.toLowerCase() ?? ""),
    fill: newestFill(record),
    /** False while the balance is unknown: an unread balance must not accuse anyone. */
    underfunded: ((): boolean => {
      if (TERMINAL_STATUSES.has(record.status?.toLowerCase() ?? "")) return false;
      const view = viewOf(record);
      const held = balances[view.sellToken];
      if (held === undefined || held === null) return false;
      const outstanding = record.remainingMakingAmount ?? view.sellAmount;
      try {
        return BigInt(outstanding) > held;
      } catch {
        return false;
      }
    })(),
  }));

  const handleEdit = (record: OrderRecord) => {
    const view = viewOf(record);
    const edit: EditIntent = {
      orderHash: record.orderHash,
      makerTraits: record.order.makerTraits,
      sellToken: view.sellToken,
      buyToken: view.buyToken,
      sellAmount: formatBaseUnitsExact(view.sellAmount, decimalsBySymbol(view.sellToken)),
    };
    navigate("/", { state: { [EDIT_INTENT_STATE_KEY]: edit } });
  };

  const busy =
    cancelState !== null && cancelState.stage !== null && !cancelState.done &&
    cancelState.error === null;

  const runCancel = async () => {
    if (cancelState === null || busy) return;
    const record = cancelState.record;
    // Busy before anything awaits, so a second click cannot start a second cancel.
    // A demo session never reaches a wallet prompt, so it is not told to confirm one.
    setCancelState({
      ...cancelState,
      stage: { kind: isDemo ? "book" : "wallet" },
      error: null,
      txHash: null,
    });
    try {
      const outcome = await cancelIntent({
        orderHash: record.orderHash,
        makerTraits: record.order.makerTraits,
        maker: walletAddress,
        onStage: (stage) => {
          setCancelState((prev) =>
            prev === null
              ? prev
              : {
                  ...prev,
                  stage,
                  txHash: stage.kind === "mining" ? stage.txHash : prev.txHash,
                },
          );
        },
      });
      setCancelState((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              done: true,
              txHash: outcome.txHash,
              bookWarning: !outcome.bookRemoved,
              error: null,
            },
      );
      await load({ silent: true });
    } catch (err) {
      if (isUserRejected(err)) {
        // Expected. Nothing was removed at the gateway: the order is still fillable.
        setCancelState((prev) =>
          prev === null
            ? prev
            : {
                ...prev,
                stage: null,
                error: "Rejected in your wallet — the intent is untouched and still live.",
              },
        );
        return;
      }
      const message =
        err instanceof CancelError ? err.message : explainWalletError(err);
      errorReporter.capture(
        `Intent cancellation failed: ${
          err instanceof CancelError ? err.message : toWalletError(err).message
        }`,
        { level: "error", meta: { source: "cancel", orderHash: record.orderHash } },
      );
      setCancelState((prev) => (prev === null ? prev : { ...prev, stage: null, error: message }));
    }
  };

  const openCancel = (record: OrderRecord) => {
    setCancelState({
      record,
      stage: null,
      txHash: null,
      error: null,
      bookWarning: false,
      done: false,
    });
  };

  const closeCancel = useCallback(() => {
    // Never mid-cancel: the dialog is the only place its outcome is reported.
    setCancelState((prev) => {
      if (prev === null) return prev;
      const inFlight = prev.stage !== null && !prev.done && prev.error === null;
      return inFlight ? prev : null;
    });
  }, []);

  // Escape closes the dialog, like every other modal on the web.
  useEffect(() => {
    if (cancelState === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cancelState, closeCancel]);

  const cancelVerb = isDemo ? "Remove from book" : "Cancel";

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Orders</h1>
        <div className="page-head-right">
          {refreshFailed && <span className="history-error">Failed to refresh</span>}
          {isDemo ? (
            <span className="page-subtitle">
              Demo intents <DemoChip />
            </span>
          ) : (
            <span className="page-subtitle" title={walletAddress}>
              Maker · {shortAddress(walletAddress)}
            </span>
          )}
          <button
            type="button"
            className="tool-button"
            onClick={() => void load()}
            aria-label="Refresh orders"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="history-card">
        {!loaded ? (
          <p className="history-empty">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="history-empty">
            No intents yet — place one on the <Link to="/">Trade screen</Link>
          </p>
        ) : (
          <div className="table-scroll">
            <table className="history-table orders-table">
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>You sell</th>
                  <th>You receive</th>
                  <th>Filled</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Created</th>
                  <th>Settlement</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ record, view, expiry, expiresAt, terminal, fill, underfunded }) => (
                  <tr key={record.orderHash} className="history-row">
                    <td>
                      <PairCell sellToken={view.sellToken} buyToken={view.buyToken} />
                    </td>
                    <td className="row-amount">
                      {formatBaseUnits(view.sellAmount, decimalsBySymbol(view.sellToken))}{" "}
                      {view.sellToken}
                    </td>
                    <td className="row-amount">
                      {formatBaseUnits(view.buyAmount, decimalsBySymbol(view.buyToken))}{" "}
                      {view.buyToken}
                    </td>
                    <td>
                      <FillCell record={record} />
                    </td>
                    <td>
                      <StatusPill status={record.status} />
                      {underfunded && (
                        <div className="cell-note" title={`Your ${view.sellToken} balance is below what this intent still sells`}>
                          not enough {view.sellToken}
                        </div>
                      )}
                    </td>
                    <td
                      className={
                        expiry?.kind === "expired" ? "cell-date cell-date-expired" : "cell-date"
                      }
                      {...(expiry === null || expiresAt === null ? {} : { title: expiresAt })}
                    >
                      {expiry?.label ?? "—"}
                    </td>
                    <td className="cell-date" title={record.createdAt}>
                      {formatRelativeTime(record.createdAt, now)}
                    </td>
                    <td className="cell-tx">
                      {fill === undefined ? (
                        <span className="cell-tx-empty">—</span>
                      ) : (
                        <TxLink txHash={fill.txHash} />
                      )}
                    </td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="tool-button"
                        onClick={() => handleEdit(record)}
                        disabled={terminal}
                        title={
                          terminal
                            ? "This intent is over — place a new one instead"
                            : "Cancel this intent and sign a replacement"
                        }
                        aria-label={`Edit intent ${shortHash(record.orderHash)}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="tool-button tool-button-danger"
                        onClick={() => openCancel(record)}
                        disabled={terminal}
                        title={
                          terminal
                            ? "This intent is over — there is nothing to cancel"
                            : isDemo
                              ? DEMO_NOTE
                              : GAS_WARNING
                        }
                        aria-label={`${cancelVerb} intent ${shortHash(record.orderHash)}`}
                      >
                        {cancelVerb}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelState !== null && (
        <div className="modal-backdrop" role="presentation" onClick={closeCancel}>
          <div
            className="modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-intent-title"
            // A click inside the card is not a click on the backdrop.
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title" id="cancel-intent-title">
              {isDemo ? "Remove from book" : "Cancel intent"} {shortHash(cancelState.record.orderHash)}
            </h2>

            {/* A demo session is warned that this does far less than it looks like. */}
            <p className="modal-text">{isDemo ? DEMO_NOTE : GAS_WARNING}</p>

            {cancelState.stage?.kind === "wallet" && (
              <div className="inline-banner inline-banner-muted" role="status">
                Confirm the cancellation in your wallet…
              </div>
            )}
            {cancelState.txHash !== null && !cancelState.done && (
              <div className="inline-banner inline-banner-muted" role="status">
                Cancellation sent · <InlineTxLink txHash={cancelState.txHash} /> — waiting for
                confirmation
              </div>
            )}
            {cancelState.done && (
              <div className="inline-banner inline-banner-success" role="status">
                {isDemo
                  ? "Removed from the gateway's book."
                  : "Cancelled on chain — this intent can no longer be filled."}
                {cancelState.txHash !== null && (
                  <>
                    {" · "}
                    <InlineTxLink txHash={cancelState.txHash} />
                  </>
                )}
              </div>
            )}
            {cancelState.bookWarning && (
              <div className="inline-banner inline-banner-warn" role="status">
                The gateway did not confirm removal from its book — the intent is dead on chain
                regardless and will drop off on the next refresh.
              </div>
            )}
            {cancelState.error !== null && (
              <div className="inline-banner inline-banner-error" role="alert">
                {cancelState.error}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="tool-button"
                onClick={closeCancel}
                disabled={busy}
              >
                {cancelState.done ? "Close" : "Keep intent"}
              </button>
              {!cancelState.done && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => void runCancel()}
                  disabled={busy}
                >
                  {cancelState.stage === null
                    ? isDemo
                      ? "Remove from book"
                      : "Cancel on chain"
                    : cancelState.stage.kind === "wallet"
                      ? "Confirm in wallet…"
                      : cancelState.stage.kind === "mining"
                        ? "Invalidating…"
                        : "Updating the book…"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
